import { Grid, GridSize, MenuItem } from "@material-ui/core";
import { Field, FormikErrors, FormikTouched } from "formik";
import * as React from "react";
import { deviceOptions, germanLvlOptions } from "../../constants";
import { t } from "../../i18n/util";
import { IGetApplicantDetailsResponse, PostApplicantPayload } from "../../types/models";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { CustomDatePicker } from "../ui/CustomDatePicker";
import { CustomInputField } from "../ui/CustomInputField";
import { CustomNumberField } from "../ui/CustomNumberField";
import { TwoFieldGridItem } from "../ui/Primitives";
import { createInputFilter } from "../util/helpers";
import { THEME } from "../../theme/ThemeStore";
import moment from "moment";

export const MAX_EXPERIENCE = 10;

type Props = {
    errors: FormikErrors<PostApplicantPayload | IGetApplicantDetailsResponse>;
    touched: FormikTouched<PostApplicantPayload | IGetApplicantDetailsResponse>;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
    lastFieldMd?: GridSize;
    children?: React.ReactNode;
};

export const DetailsGrid = ({ errors, touched, setFieldValue, lastFieldMd, children }: Props) => {
    const isMobile = useMobileMediaQuery();
    const isDal = THEME.THEME_NAME === "dal";

    React.useEffect(() => {
        createInputFilter(document.getElementById("salary"), function (value) {
            return /^\+?\d{0,6}$/.test(value);
        });

        createInputFilter(document.getElementById("noticePeriod"), function (value) {
            return /^\+?\d{0,2}$/.test(value);
        });

        createInputFilter(document.getElementById("workHours"), function (value) {
            return /^\+?\d{0,2}[.,]\d?$/.test(value) || /^\+?\d{0,2}$/.test(value);
        });
    }, []);

    // HRB-432: Limit start date to two years in the future
    const maxDate = moment().add(2, "years").toDate();

    return (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <TwoFieldGridItem>
                <Field
                    component={CustomInputField}
                    label={isMobile ? t("screen.details.salary.mobile") : t("screen.details.salary")}
                    name="salary"
                    inputProps={{ id: "salary" }}
                    errorMessage={errors.salary}
                    required
                    isTouched={touched.salary}
                />
            </TwoFieldGridItem>
            <TwoFieldGridItem>
                <Field
                    component={CustomNumberField}
                    maxAmount={MAX_EXPERIENCE}
                    label={t("screen.details.workExperience")}
                    name="experience"
                    changeValue={(value: number) => {
                        setFieldValue("experience", value);
                    }}
                />
            </TwoFieldGridItem>
            <TwoFieldGridItem>
                <Field
                    component={CustomDatePicker}
                    label={t("screen.details.startDate")}
                    name="startDate"
                    disablePast
                    maxDate={maxDate}
                    errorMessage={errors.startDate}
                    isTouched={touched.startDate}
                />
            </TwoFieldGridItem>

            <TwoFieldGridItem>
                <Field
                    component={CustomInputField}
                    label={t("screen.details.noticePeriod")}
                    name="noticePeriod"
                    inputProps={{ id: "noticePeriod" }}
                    errorMessage={errors.noticePeriod}
                    isTouched={touched.noticePeriod}
                    optional
                />
            </TwoFieldGridItem>

            {!isDal ? (
                <TwoFieldGridItem>
                    <Field
                        component={CustomInputField}
                        label={isMobile ? t("screen.details.workHours.mobile") : t("screen.details.workHours")}
                        name="workHours"
                        required
                        inputProps={{ id: "workHours" }}
                        errorMessage={errors.workHours}
                        isTouched={touched.workHours}
                    />
                </TwoFieldGridItem>
            ) : (
                <Grid item xs={12} sm={12} md={lastFieldMd ?? 12}>
                    <Field
                        component={CustomInputField}
                        label={isMobile ? t("screen.details.workHours.mobile") : t("screen.details.workHours")}
                        name="workHours"
                        required
                        inputProps={{ id: "workHours" }}
                        errorMessage={errors.workHours}
                        isTouched={touched.workHours}
                    />
                </Grid>
            )}

            {!isDal && (
                <>
                    <TwoFieldGridItem>
                        <Field
                            component={CustomInputField}
                            select
                            label={t("screen.details.language")}
                            required
                            name="germanLvl"
                            errorMessage={errors.germanLvl}
                            isTouched={touched.germanLvl}
                        >
                            {germanLvlOptions.map((option, index) => (
                                <MenuItem key={index} value={option.value}>
                                    {t(option.messageId)}
                                </MenuItem>
                            ))}
                        </Field>
                    </TwoFieldGridItem>
                    <Grid item xs={12} sm={12} md={lastFieldMd ?? 12}>
                        <Field
                            component={CustomInputField}
                            label={t("screen.details.device")}
                            select
                            name="device"
                            required
                            errorMessage={errors.device}
                            isTouched={touched.device}
                        >
                            {deviceOptions.map((option, index) => (
                                <MenuItem key={index} value={option.value}>
                                    {t(option.messageId)}
                                </MenuItem>
                            ))}
                        </Field>
                    </Grid>
                </>
            )}
            {children}
        </Grid>
    );
};
